import React, { Dispatch, SetStateAction, useEffect } from 'react';
import AppConfigurationService from '@/services/app-configuration-service/app-configuration-service';
import { ModalContextProps, useModalContext } from '@contexts/modalContext';
import { SecurityContent } from '@sections/sign-in-and-security/hook/use-security-content';
import PasskeyIcon from '@views/sign-in-credentials-view/passkeys/passkey-icon/passkey-icon';
import PrimaryButton from '@common/primary-button/primary-button';
import TertiaryButton from '@common/tertiary-button/tertiary-button';
import { NotificationType } from '@/contexts/notificationContext';
import RemovePasskeyModalContent from '@views/sign-in-credentials-view/passkeys/remove-passkeys-modal/remove-passkeys-modal-content';
import ScrollUtil from '@utils/scroll-to-top-util/scroll-to-top-util';
import './passkeys-section.scss';
import AuthenticationService from '@services/authentication-service/authentication-service';
import { CookieUtils } from '@utils/cookie-utils/cookie-utils';
import { PKCE, SESSION_KEYS } from '@constants';
import { useSearchParams } from 'react-router-dom';
import { formatTimestamp } from '@utils/date-time-utils/date-time-utils';
import { useAnalytics } from '@/hooks/use-analytics';

export interface PasskeysSectionProps {
    securityContent: SecurityContent;
    hasPasskey: boolean;
    passkeyRegistrationTimestamp: string;
    isRtlWebview?: boolean;
    setPasskeysNotification: Dispatch<SetStateAction<PasskeyNotifications>>;
}

export interface PasskeyNotifications {
    notificationType: NotificationType;
    header: string;
    description: string;
}

export enum PasskeyActions {
    ADD = 'add',
    REMOVE = 'remove',
}

const PasskeysSection = (props: PasskeysSectionProps) => {
    const {
        securityContent,
        hasPasskey,
        setPasskeysNotification,
        passkeyRegistrationTimestamp,
        isRtlWebview,
    } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const appConfig = new AppConfigurationService();
    const isLincoln = appConfig.brand === 'lincoln';
    const { setContext, resetContext } = useModalContext();
    const scrollUtil = new ScrollUtil();
    const hasAdded = searchParams.has('passkeyAdd');
    const hasRemoved = searchParams.has('passkeyRemove');
    const authenticationService = new AuthenticationService();
    const [fireAnalytics] = useAnalytics();
    const splitLocale: string[] =
        appConfig.currentLanguageRegionCode.split('-');
    const formattedIsoLocale = `${
        splitLocale[0]
    }-${splitLocale[1].toUpperCase()}`;
    const userTimeZone: string =
        typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : 'UTC';

    const cleanSearchParams = (): void => {
        sessionStorage.removeItem(SESSION_KEYS.PASSKEY_FLOW_RETURN);

        const nextParams = new URLSearchParams(searchParams.toString());
        nextParams.delete('passkeyAdd');
        nextParams.delete('passkeyRemove');

        const paramsObject: Record<string, string> = {};
        nextParams.forEach((value, key) => {
            paramsObject[key] = value;
        });

        setSearchParams(paramsObject, { replace: true });
    };

    const getEffectiveDomain = (): string => {
        const serverContext = (window as any).SERVER_CONTEXT;
        if (serverContext?.domain) {
            return serverContext.domain;
        }

        return window.location.hostname;
    };

    const createPasskeyClickHandler = (): void => {
        fireAnalytics('signinCredentialsCreatePasskeyOnclickEvent');
        authenticationService
            .generatePasskeysUrlAndVerifier(PasskeyActions.ADD)
            .then(({ verifier, fmaPasskeysUrls }) => {
                const effectiveDomain = getEffectiveDomain();

                CookieUtils.setFMACookie(
                    PKCE.VERIFIER,
                    verifier,
                    effectiveDomain,
                    60
                );

                if (CookieUtils.has(PKCE.VERIFIER)) {
                    sessionStorage.setItem(
                        SESSION_KEYS.PASSKEY_FLOW_RETURN,
                        'true'
                    );
                    window.location.assign(
                        fmaPasskeysUrls.passkeyAdd.toString()
                    );
                }
            });
    };

    const removePasskeyClickHandler = (): void => {
        fireAnalytics('signinCredentialsRemovePasskeyOnclickEvent');
        authenticationService
            .generatePasskeysUrlAndVerifier(PasskeyActions.REMOVE)
            .then(({ verifier, fmaPasskeysUrls }) => {
                const effectiveDomain = getEffectiveDomain();

                CookieUtils.setFMACookie(
                    PKCE.VERIFIER,
                    verifier,
                    effectiveDomain,
                    60
                );

                if (CookieUtils.has(PKCE.VERIFIER)) {
                    sessionStorage.setItem(
                        SESSION_KEYS.PASSKEY_FLOW_RETURN,
                        'true'
                    );
                    window.location.assign(
                        fmaPasskeysUrls.passkeyRemove.toString()
                    );
                }
            });
    };

    const createPasskeySuccessActions = (): void => {
        setPasskeysNotification({
            notificationType: NotificationType.Success,
            header: securityContent?.passkeysCreationSuccessNotificationHeader,
            description:
                securityContent?.passkeysCreationSuccessNotificationDescription,
        });

        cleanSearchParams();
        scrollUtil.scrollPageToTop();
    };

    const removalSuccessActions = (): void => {
        setPasskeysNotification({
            notificationType: NotificationType.Success,
            header: securityContent?.passkeysRemovalSuccessNotificationHeader,
            description:
                securityContent?.passkeysRemovalSuccessNotificationDescription,
        });
        cleanSearchParams();
    };

    const removalFailureActions = (): void => {
        setPasskeysNotification({
            notificationType: NotificationType.Error,
            header: securityContent?.passkeysRemovalErrorNotificationHeader,
            description:
                securityContent?.passkeysRemovalErrorNotificationDescription,
        });
        cleanSearchParams();
    };

    const removePasskeyModalProps: ModalContextProps = {
        modalType: {
            name: 'remove-passkey-modal',
            className: isRtlWebview
                ? 'rtl-sign-in-credentials-modal'
                : undefined,
            primaryButtonLabel: securityContent?.passkeysRemoveButton,
            secondaryButtonLabel: securityContent?.cancelButton,
            primaryButtonAriaLabel: securityContent?.passkeysRemoveButton,
            secondaryButtonAriaLabel: securityContent?.cancelButton,
            closeButtonAriaLabel: securityContent?.cancelButton,
            onPrimaryButtonClick: () => {
                removePasskeyClickHandler();

                resetContext();
                scrollUtil.scrollPageToTop();
            },
            onSecondaryButtonClick: resetContext,
            onAfterClose: resetContext,
            children: (
                <RemovePasskeyModalContent
                    header={securityContent?.passkeysRemoveModalHeader}
                    description={
                        securityContent?.passkeysRemoveModalDescription
                    }
                />
            ),
            isLincolnNvc: isLincoln,
            hideChevron: true,
            preventClose: false,
        },
    };

    useEffect(() => {
        if (hasAdded) {
            if (hasPasskey) {
                createPasskeySuccessActions();
            } else {
                cleanSearchParams();
            }
        } else if (hasRemoved) {
            if (hasPasskey) {
                removalFailureActions();
            } else {
                removalSuccessActions();
            }
        }
    }, [hasAdded, hasRemoved, hasPasskey]);

    return (
        <div className="passkeys-section" data-testid="passkeys-section">
            <h2
                className="passkeys-section__header"
                data-testid="passkeys-section__header"
            >
                {securityContent?.passkeysSectionHeader}
            </h2>

            {!hasPasskey && (
                <>
                    <div className="passkeys-section__subheadings">
                        <p
                            className="icon-subheading"
                            data-testid="passkeys-section__subheading-1"
                        >
                            <PasskeyIcon iconType={1} />
                            <span>
                                {securityContent?.passkeysIconSubheadingOne}
                            </span>
                        </p>
                        <p
                            className="icon-subheading"
                            data-testid="passkeys-section__subheading-2"
                        >
                            <PasskeyIcon iconType={2} />
                            <span>
                                {securityContent?.passkeysIconSubheadingTwo}
                            </span>
                        </p>
                        <p
                            className="icon-subheading"
                            data-testid="passkeys-section__subheading-3"
                        >
                            <PasskeyIcon iconType={3} />
                            <span>
                                {securityContent?.passkeysIconSubheadingThree}
                            </span>
                        </p>
                    </div>

                    <p
                        className="passkeys-section__description"
                        data-testid="passkeys-section__description"
                    >
                        {securityContent?.passkeysPreDescription}
                    </p>

                    {securityContent?.passkeysCreateButton && (
                        <PrimaryButton
                            className="create-passkey-button"
                            testId="create-passkey-button"
                            onClick={createPasskeyClickHandler}
                            aria-label={securityContent?.passkeysCreateButton}
                            tabIndex={0}
                            newLincolnButton={isLincoln}
                            hideChevron={true}
                        >
                            {securityContent?.passkeysCreateButton}
                        </PrimaryButton>
                    )}
                </>
            )}
            {hasPasskey && (
                <>
                    <p
                        className="passkeys-section__post-description"
                        data-testid="passkeys-section__post-description"
                    >
                        {securityContent?.passkeysPostDescription}
                    </p>

                    <div className="passkeys-section__has-passkey">
                        <h3
                            className="passkeys-section__id"
                            data-testid="passkeys-section-id"
                        >
                            {securityContent?.passkeysIdText}
                        </h3>

                        {passkeyRegistrationTimestamp && (
                            <p
                                className="passkeys-section__created-on-date"
                                data-testid="passkeys-section-created-on-date"
                            >
                                {securityContent?.passkeysCreatedText}{' '}
                                {formatTimestamp(
                                    passkeyRegistrationTimestamp,
                                    formattedIsoLocale,
                                    { timeZone: userTimeZone }
                                )}
                            </p>
                        )}

                        {securityContent?.passkeysRemoveButton && (
                            <TertiaryButton
                                className="remove-passkey-button"
                                dataTestId="remove-passkey-button"
                                ariaLabel={
                                    securityContent?.passkeysRemoveButton
                                }
                                labelText={
                                    securityContent?.passkeysRemoveButton
                                }
                                internal={true}
                                onClick={() =>
                                    setContext(removePasskeyModalProps)
                                }
                            />
                        )}
                    </div>
                    <hr className="passkeys-section__divider" />
                </>
            )}
        </div>
    );
};

export default PasskeysSection;
